import React, { useState, useRef, useEffect } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { 
  PlayArrow, 
  Pause, 
  Stop, 
  VolumeUp, 
  Speed, 
  Settings,
  Mic,
  VolumeMute,
  ExpandMore,
  ExpandLess,
  Close
} from '@mui/icons-material';
import apiService from '../services/api';

const TTSPlayer = ({ content, className = "", compact = false }) => {
  const [isPlaying, setIsPlaying] = useState(false);
  const [isLoading, setIsLoading] = useState(false);
  const [selectedVoice, setSelectedVoice] = useState('en-IN');
  const [speed, setSpeed] = useState(1.0);
  const [volume, setVolume] = useState(0.8);
  const [audioUrl, setAudioUrl] = useState(null);
  const [error, setError] = useState('');
  const [showControls, setShowControls] = useState(false);
  const [isExpanded, setIsExpanded] = useState(false);
  const [availableVoices, setAvailableVoices] = useState([]);
  const [healthStatus, setHealthStatus] = useState(null);
  const [currentWordIndex, setCurrentWordIndex] = useState(-1);
  const [textWords, setTextWords] = useState([]);
  
  const audioRef = useRef(null);
  const progressRef = useRef(null);

  useEffect(() => {
    fetchHealthStatus();
    fetchAvailableVoices();
  }, []);

  const fetchHealthStatus = async () => {
    try {
      const response = await apiService.getTTSHealth();
      setHealthStatus(response);
    } catch (error) {
      console.error('Health check failed:', error);
      setHealthStatus({ status: 'error', message: 'TTS service unavailable' });
    }
  };

  const fetchAvailableVoices = async () => {
    try {
      const response = await apiService.getTTSVoices();
      setAvailableVoices(response.voices || []);
    } catch (error) {
      console.error('Failed to fetch voices:', error);
    }
  };

  const generateTTS = async (textContent, voice, speed) => {
    setIsLoading(true);
    setError('');
    
    try {
      // Chunk the text if it's too long
      const textChunks = chunkTextForTTS(textContent);
      
      if (textChunks.length === 1) {
        // Single chunk - generate normally
        const blob = await apiService.streamTTSAudio(textChunks[0], voice, speed);
        const url = URL.createObjectURL(blob);
        setAudioUrl(url);
        return url;
      } else {
        // Multiple chunks - generate first chunk for now
        console.log(`Text too long (${textContent.length} chars), using first ${textChunks.length} chunks`);
        setError(`Text is very long (${textContent.length} characters). Playing first part. Consider reading shorter sections.`);
        
        const blob = await apiService.streamTTSAudio(textChunks[0], voice, speed);
        const url = URL.createObjectURL(blob);
        setAudioUrl(url);
        return url;
      }
    } catch (error) {
      console.error('TTS generation failed:', error);
      
      // If the error is about text being too long, try with a shorter version
      if (error.message.includes('Text too long') || error.message.includes('400')) {
        try {
          // Try with just the first 2000 characters
          const shortText = textContent.substring(0, 2000) + '...';
          console.log('Retrying with shorter text:', shortText.length, 'characters');
          
          const blob = await apiService.streamTTSAudio(shortText, voice, speed);
          const url = URL.createObjectURL(blob);
          setAudioUrl(url);
          setError('Using shortened version due to length limits.');
          return url;
        } catch (retryError) {
          console.error('Retry also failed:', retryError);
          setError('Failed to generate audio. Text may be too long or complex.');
          return null;
        }
      } else {
        setError('Failed to generate audio. Please try again.');
        return null;
      }
    } finally {
      setIsLoading(false);
    }
  };

  // Helper function to extract text content from various content formats
  const extractTextContent = (content) => {
    if (!content) return '';
    
    // If content is a string, return it directly
    if (typeof content === 'string') {
      return content.trim();
    }
    
    // If content is an object with structured format
    if (typeof content === 'object') {
      let textParts = [];
      
      // Add summary if available (prioritize summary for TTS)
      if (content.summary) {
        textParts.push(`Summary: ${content.summary}`);
      }
      
      // Add key points if available (good for TTS)
      if (content.key_points && Array.isArray(content.key_points)) {
        textParts.push(`Key Points: ${content.key_points.join('. ')}`);
      }
      
      // Add title if available
      if (content.title) {
        textParts.push(`Title: ${content.title}`);
      }
      
      // Add detailed content only if we don't have summary or key points
      if (content.detailed_content && textParts.length === 0) {
        textParts.push(`Detailed Content: ${content.detailed_content}`);
      }
      
      // If no structured content, try to stringify the object
      if (textParts.length === 0) {
        return JSON.stringify(content);
      }
      
      return textParts.join('. ');
    }
    
    // Fallback: convert to string
    return String(content).trim();
  };

  // Helper function to chunk text for TTS (max 4000 chars to be safe)
  const chunkTextForTTS = (text, maxLength = 4000) => {
    if (text.length <= maxLength) {
      return [text];
    }
    
    const chunks = [];
    const sentences = text.split(/[.!?]+/).filter(s => s.trim().length > 0);
    let currentChunk = '';
    
    for (const sentence of sentences) {
      const sentenceWithPunctuation = sentence.trim() + '.';
      if (currentChunk.length + sentenceWithPunctuation.length <= maxLength) {
        currentChunk += sentenceWithPunctuation + ' ';
      } else {
        if (currentChunk.trim()) {
          chunks.push(currentChunk.trim());
        }
        currentChunk = sentenceWithPunctuation + ' ';
      }
    }
    
    if (currentChunk.trim()) {
      chunks.push(currentChunk.trim());
    }
    
    return chunks.length > 0 ? chunks : [text.substring(0, maxLength)];
  };

  const handlePlay = async () => {
    const textContent = extractTextContent(content);
    if (!textContent) {
      setError('No content available to convert to speech.');
      return;
    }

    // Check if text is too long and provide feedback
    if (textContent.length > 4000) {
      console.log(`Long text detected: ${textContent.length} characters`);
    }

    // Split text into words for highlighting
    const words = textContent.split(/\s+/).filter(word => word.length > 0);
    setTextWords(words);
    setCurrentWordIndex(-1);

    if (audioRef.current && audioUrl) {
      // Resume existing audio
      audioRef.current.play();
      setIsPlaying(true);
    } else {
      // Generate new audio
      const url = await generateTTS(textContent, selectedVoice, speed);
      if (url) {
        audioRef.current.src = url;
        audioRef.current.volume = volume;
        audioRef.current.play();
        setIsPlaying(true);
      }
    }
  };

  const handlePause = () => {
    if (audioRef.current) {
      audioRef.current.pause();
      setIsPlaying(false);
    }
  };

  const handleStop = () => {
    if (audioRef.current) {
      audioRef.current.pause();
      audioRef.current.currentTime = 0;
      setIsPlaying(false);
    }
  };

  const handleSpeedChange = (newSpeed) => {
    setSpeed(newSpeed);
    if (audioRef.current) {
      audioRef.current.playbackRate = newSpeed;
    }
  };

  const handleVolumeChange = (newVolume) => {
    setVolume(newVolume);
    if (audioRef.current) {
      audioRef.current.volume = newVolume;
    }
  };

  const handleVoiceChange = (voice) => {
    setSelectedVoice(voice);
    // Stop current audio when voice changes
    if (audioRef.current) {
      handleStop();
      setAudioUrl(null);
    }
  };

  const handleAudioEnded = () => {
    setIsPlaying(false);
    setCurrentWordIndex(-1);
  };

  const handleAudioTimeUpdate = () => {
    if (audioRef.current && progressRef.current) {
      const progress = (audioRef.current.currentTime / audioRef.current.duration) * 100;
      progressRef.current.style.width = `${progress}%`;
      
      // Calculate current word based on audio progress
      if (textWords.length > 0 && audioRef.current.duration) {
        const currentTime = audioRef.current.currentTime;
        const totalDuration = audioRef.current.duration;
        const progressPercent = currentTime / totalDuration;
        const wordIndex = Math.floor(progressPercent * textWords.length);
        setCurrentWordIndex(Math.min(wordIndex, textWords.length - 1));
      }
    }
  };

  const handleSeek = (e) => {
    if (audioRef.current) {
      const rect = e.currentTarget.getBoundingClientRect();
      const percent = (e.clientX - rect.left) / rect.width;
      audioRef.current.currentTime = percent * audioRef.current.duration;
    }
  };

  // Function to render highlighted text
  const renderHighlightedText = () => {
    if (!content || textWords.length === 0) return null;
    
    const textContent = extractTextContent(content);
    if (!textContent) return null;
    
    // Split the text and highlight current word
    const words = textContent.split(/(\s+)/);
    
    return (
      <div className="fixed left-4 top-4 max-w-md bg-white/95 dark:bg-gray-900/95 backdrop-blur-xl rounded-xl shadow-2xl border border-gray-200 dark:border-gray-700 p-4 z-[9998] pointer-events-none">
        <div className="text-sm text-gray-700 dark:text-gray-300 leading-relaxed">
          {words.map((word, index) => {
            const isCurrentWord = index === currentWordIndex * 2; // Account for spaces
            return (
              <span
                key={index}
                className={`transition-all duration-200 ${
                  isCurrentWord && isPlaying
                    ? 'bg-yellow-300 dark:bg-yellow-600 text-black dark:text-white font-bold px-1 rounded'
                    : ''
                }`}
              >
                {word}
              </span>
            );
          })}
        </div>
      </div>
    );
  };

  // Don't render if TTS is not available or no content
  if (!healthStatus || healthStatus.status !== 'healthy' || !content) {
    return null;
  }

  // Compact mode for header
  if (compact) {
    return (
      <div className="flex items-center space-x-2 bg-white/80 dark:bg-gray-800/80 backdrop-blur-sm rounded-lg px-3 py-2 border border-gray-200 dark:border-gray-700">
        {/* Play/Pause Button */}
        <button
          onClick={isPlaying ? handlePause : handlePlay}
          disabled={isLoading}
          className="w-8 h-8 bg-gradient-to-r from-blue-500 to-purple-500 text-white rounded-full hover:from-blue-600 hover:to-purple-600 transition-all duration-200 disabled:opacity-50 shadow-sm hover:shadow-md transform hover:scale-105 active:scale-95 flex items-center justify-center"
          title={isPlaying ? 'Pause' : 'Play'}
        >
          {isLoading ? (
            <div className="w-3 h-3 border-2 border-white border-t-transparent rounded-full animate-spin" />
          ) : isPlaying ? (
            <Pause className="w-3 h-3" />
          ) : (
            <PlayArrow className="w-3 h-3" />
          )}
        </button>
        
        {/* Stop Button */}
        <button
          onClick={handleStop}
          disabled={!isPlaying}
          className="w-8 h-8 bg-gray-500 text-white rounded-full hover:bg-gray-600 transition-all duration-200 disabled:opacity-50 shadow-sm hover:shadow-md transform hover:scale-105 active:scale-95 flex items-center justify-center"
          title="Stop"
        >
          <Stop className="w-3 h-3" />
        </button>
        
        {/* Speed Control */}
        <div className="flex items-center space-x-1">
          <Speed className="w-3 h-3 text-gray-500" />
          <select
            value={speed}
            onChange={(e) => handleSpeedChange(parseFloat(e.target.value))}
            className="text-xs bg-transparent border-none focus:ring-0 text-gray-700 dark:text-gray-300"
          >
            <option value={0.5}>0.5x</option>
            <option value={0.75}>0.75x</option>
            <option value={1.0}>1.0x</option>
            <option value={1.25}>1.25x</option>
            <option value={1.5}>1.5x</option>
            <option value={2.0}>2.0x</option>
          </select>
        </div>
        
        {/* Error Display */}
        {error && (
          <div className="text-xs text-red-500 bg-red-50 dark:bg-red-900/20 px-2 py-1 rounded max-w-32 truncate" title={error}>
            {error.length > 30 ? error.substring(0, 30) + '...' : error}
          </div>
        )}
      </div>
    );
  }

  return (
    <>
      {/* Highlighted Text Display */}
      {isPlaying && renderHighlightedText()}
      
      {/* Backdrop Overlay when Expanded */}
      {isExpanded && (
        <motion.div
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          exit={{ opacity: 0 }}
          transition={{ duration: 0.3 }}
          className="fixed inset-0 bg-black/20 backdrop-blur-sm z-40"
          onClick={() => setIsExpanded(false)}
        />
      )}
      
      {/* TTS Player */}
      <motion.div
        initial={{ opacity: 0, x: 50, scale: 0.95 }}
        animate={{ opacity: 1, x: 0, scale: 1 }}
        transition={{ 
          duration: 0.5,
          ease: [0.4, 0.0, 0.2, 1],
          type: "spring",
          stiffness: 100,
          damping: 20
        }}
        className="relative z-10"
        style={{ 
          position: 'relative',
          zIndex: isExpanded ? 50 : 10
        }}
      >
        <motion.div 
          className={`bg-transparent backdrop-blur-sm rounded-2xl overflow-hidden ${
            isExpanded ? 'absolute top-0 right-0 z-50' : 'relative'
          }`}
          animate={{ 
            width: isExpanded ? 400 : 206,
            height: isExpanded ? "auto" : 90
          }}
          transition={{ 
            duration: 0.5, 
            ease: [0.4, 0.0, 0.2, 1], // Custom easing for smooth animation
            type: "spring",
            stiffness: 100,
            damping: 20
          }}
        >
        {/* Collapsed State */}
        {!isExpanded && (
          <motion.div
            initial={{ opacity: 0, scale: 0.95 }}
            animate={{ opacity: 1, scale: 1 }}
            exit={{ opacity: 0, scale: 0.95 }}
            transition={{ 
              duration: 0.4,
              ease: [0.4, 0.0, 0.2, 1],
              delay: 0.1
            }}
            className="p-4 h-full flex flex-col justify-center"
          >
            <div className="h-full flex flex-col justify-center items-center relative">
              {/* Horizontal Line with All Elements */}
              <div className="flex items-center justify-between w-full px-3">
                {/* Expand Button */}
                <button
                  onClick={() => setIsExpanded(true)}
                  className="p-1 bg-white/60 dark:bg-gray-800/60 text-gray-700 dark:text-gray-200 hover:text-gray-900 dark:hover:text-white transition-all duration-200 rounded-full hover:bg-white/80 dark:hover:bg-gray-700/80 group z-10"
                  title="Expand controls"
                >
                  <ExpandMore className="w-3.5 h-3.5 group-hover:scale-110 transition-transform" />
                </button>
                
                {/* Play/Pause Button */}
                <button
                  onClick={isPlaying ? handlePause : handlePlay}
                  disabled={isLoading}
                  className="w-10 h-10 bg-gradient-to-r from-purple-500 to-pink-500 text-white rounded-full hover:from-purple-600 hover:to-pink-600 transition-all duration-200 disabled:opacity-50 shadow-lg hover:shadow-xl transform hover:scale-110 active:scale-95 flex items-center justify-center"
                >
                  {isLoading ? (
                    <div className="w-4 h-4 border-2 border-white border-t-transparent rounded-full animate-spin" />
                  ) : isPlaying ? (
                    <Pause className="w-4 h-4" />
                  ) : (
                    <PlayArrow className="w-4 h-4" />
                  )}
                </button>
                
                {/* Stop Button */}
                <button
                  onClick={handleStop}
                  disabled={!isPlaying && !audioUrl}
                  className="w-8 h-8 bg-gray-500 hover:bg-gray-600 text-white rounded-full transition-all duration-200 disabled:opacity-30 disabled:cursor-not-allowed shadow-lg hover:shadow-xl transform hover:scale-110 active:scale-95 flex items-center justify-center"
                  title="Stop"
                >
                  <Stop className="w-3.5 h-3.5" />
                </button>
                
                {/* Status Indicator */}
                {healthStatus && healthStatus.status === 'healthy' && (
                  <div 
                    className="w-3 h-3 bg-green-500 rounded-full border border-white dark:border-gray-900 cursor-help"
                    title="Audio Service Available"
                  ></div>
                )}
              </div>

              {/* Progress Bar */}
              <div 
                className="w-full h-1.5 bg-gray-200/50 dark:bg-gray-700/50 rounded-full cursor-pointer mt-3 overflow-hidden"
                onClick={handleSeek}
              >
                <div
                  ref={progressRef}
                  className="h-full bg-gradient-to-r from-purple-500 to-pink-500 rounded-full transition-all duration-200"
                  style={{ width: '0%' }}
                />
              </div>

              {/* Error Display */}
              {error && (
                <div className="mt-3 p-2 bg-red-50 dark:bg-red-900/20 border border-red-200 dark:border-red-700/50 rounded-lg text-red-700 dark:text-red-300 text-xs">
                  {error}
                </div>
              )}
            </div>
          </motion.div>
        )}

        {/* Expanded State */}
        {isExpanded && (
          <motion.div
            initial={{ opacity: 0, scale: 0.95, y: 10 }}
            animate={{ opacity: 1, scale: 1, y: 0 }}
            exit={{ opacity: 0, scale: 0.95, y: 10 }}
            transition={{ 
              duration: 0.4,
              ease: [0.4, 0.0, 0.2, 1],
              delay: 0.05
            }}
            className="p-4 min-h-[300px]"
          >
              {/* Header */}
              <div className="flex items-center justify-between mb-4">
                <div className="flex items-center gap-3">
                  <div className="relative">
                    <div className="w-10 h-10 bg-gradient-to-br from-purple-500 to-pink-500 rounded-xl flex items-center justify-center shadow-lg">
                      <Mic className="w-5 h-5 text-white" />
                    </div>
                    {healthStatus && healthStatus.status === 'healthy' && (
                      <div className="absolute -top-1 -right-1 w-4 h-4 bg-green-500 rounded-full border-2 border-white dark:border-gray-900 flex items-center justify-center">
                        <div className="w-2 h-2 bg-white rounded-full"></div>
                      </div>
                    )}
                  </div>
                  <div>
                    <div className="text-lg font-semibold text-gray-800 dark:text-gray-200">
                      Audio Controls
                    </div>
                    <div className="text-sm text-gray-500 dark:text-gray-400">
                      {healthStatus?.status === 'healthy' ? 'Ready to play' : 'Connecting...'}
                    </div>
                  </div>
                </div>
                
                <button
                  onClick={() => setIsExpanded(false)}
                  className="p-2 text-gray-500 dark:text-gray-400 hover:text-gray-700 dark:hover:text-gray-200 transition-all duration-200 rounded-xl hover:bg-gray-100/50 dark:hover:bg-gray-700/50 group"
                >
                  <Close className="w-5 h-5 group-hover:scale-110 transition-transform" />
                </button>
              </div>



              {/* Progress Bar */}
              <div 
                className="w-full h-2 bg-gray-200/50 dark:bg-gray-700/50 rounded-full cursor-pointer mb-3"
                onClick={handleSeek}
              >
                <div
                  ref={progressRef}
                  className="h-full bg-gradient-to-r from-purple-500 to-pink-500 rounded-full transition-all duration-200 shadow-sm"
                  style={{ width: '0%' }}
                />
              </div>

              {/* Error Display */}
              {error && (
                <div className="mb-3 p-3 bg-red-50 dark:bg-red-900/20 border border-red-200 dark:border-red-700/50 rounded-xl text-red-700 dark:text-red-300 text-xs">
                  {error}
                </div>
              )}

              {/* Progress Bar */}
              <div 
                className="w-full h-2 bg-gray-200 dark:bg-gray-700 rounded-full cursor-pointer mb-4"
                onClick={handleSeek}
              >
                <div
                  ref={progressRef}
                  className="h-full bg-gradient-to-r from-purple-500 to-pink-500 rounded-full transition-all duration-200"
                  style={{ width: '0%' }}
                />
              </div>

              {/* Error Display */}
              {error && (
                <div className="mb-4 p-2 bg-red-100 dark:bg-red-900/30 border border-red-200 dark:border-red-700 rounded-lg text-red-800 dark:text-red-300 text-xs">
                  {error}
                </div>
              )}

              {/* Controls */}
              <div className="space-y-3">
                {/* Voice Selection */}
                <div>
                  <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                    Voice
                  </label>
                  <div className="grid grid-cols-2 gap-2">
                    {availableVoices.slice(0, 4).map((voice, index) => {
                      // Map technical voice names to owl-themed names
                      const owlNames = ['Hoot', 'Wise', 'Nest', 'Feather'];
                      const displayName = owlNames[index] || voice.name;
                      
                      return (
                        <button
                          key={voice.name}
                          onClick={() => handleVoiceChange(voice.name)}
                          className={`p-2 rounded-lg text-xs transition-all duration-200 ${
                            selectedVoice === voice.name
                              ? 'bg-purple-100 dark:bg-purple-900/30 text-purple-700 dark:text-purple-300 border border-purple-300 dark:border-purple-600'
                              : 'bg-gray-100 dark:bg-gray-700 text-gray-600 dark:text-gray-300 hover:bg-purple-50 dark:hover:bg-purple-900/20'
                          }`}
                        >
                          {displayName}
                        </button>
                      );
                    })}
                  </div>
                </div>

                {/* Speed Control */}
                <div>
                  <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1 flex items-center gap-1">
                    <Speed className="w-4 h-4" />
                    Speed: {speed}x
                  </label>
                  <input
                    type="range"
                    min="0.5"
                    max="2.0"
                    step="0.1"
                    value={speed}
                    onChange={(e) => handleSpeedChange(parseFloat(e.target.value))}
                    className="w-full h-2 bg-gray-200 dark:bg-gray-700 rounded-lg appearance-none cursor-pointer slider"
                  />
                  <div className="flex justify-between text-xs text-gray-500 dark:text-gray-400 mt-1">
                    <span>0.5x</span>
                    <span>1.0x</span>
                    <span>2.0x</span>
                  </div>
                </div>

                {/* Volume Control */}
                <div>
                  <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1 flex items-center gap-1">
                    <VolumeUp className="w-4 h-4" />
                    Volume: {Math.round(volume * 100)}%
                  </label>
                  <div className="flex items-center gap-2">
                    <VolumeMute className="w-4 h-4 text-gray-400" />
                    <input
                      type="range"
                      min="0"
                      max="1"
                      step="0.1"
                      value={volume}
                      onChange={(e) => handleVolumeChange(parseFloat(e.target.value))}
                      className="flex-1 h-2 bg-gray-200 dark:bg-gray-700 rounded-lg appearance-none cursor-pointer slider"
                    />
                    <VolumeUp className="w-4 h-4 text-gray-400" />
                  </div>
                </div>


              </div>
            </motion.div>
          )}

        {/* Audio Element */}
        <audio
          ref={audioRef}
          onEnded={handleAudioEnded}
          onTimeUpdate={handleAudioTimeUpdate}
          className="hidden"
        />
      </motion.div>
    </motion.div>
    </>
  );
};

export default TTSPlayer; 